"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { VisuallyHidden } from "radix-ui";
import { buttonVariants } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Modal, ModalContent, ModalTitle } from "@/components/ui/modal";
import { cn } from "@/lib/utils";
import type { VideoPlayerProps } from "@/components/ui/video-player";

const VideoPlayer = dynamic(
  () => import("@/components/ui/video-player").then((mod) => mod.VideoPlayer),
  { ssr: false },
) as React.ForwardRefExoticComponent<
  VideoPlayerProps & React.RefAttributes<HTMLVideoElement>
>;

export interface AcademyContent {
  type: string;
  typeLabel: string;
  title: string;
  description: string;
  duration: string;
  videoUrl: string;
  thumbnailUrl: string;
  accentColor: string;
  typeColor: string;
  docsUrl?: string;
}

export interface ArbiAcademyModalProps {
  open: boolean;
  onClose: () => void;
  content: AcademyContent;
}

export function ArbiAcademyModal({
  open,
  onClose,
  content,
}: ArbiAcademyModalProps) {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  /* eslint-disable react-hooks/set-state-in-effect -- reset thumbnail when open becomes false (parent-controlled or Radix close) */
  useEffect(() => {
    if (!open) {
      setPlaying(false);
    }
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handlePlay = useCallback(() => {
    setPlaying(true);
    setTimeout(() => {
      videoRef.current?.play();
    }, 100);
  }, []);

  const dialogTitle = content.title
    ? `Arbi Academy: ${content.title}`
    : "Arbi Academy";

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <ModalContent
        size="md"
        className="flex max-h-[92dvh] w-full flex-col overflow-hidden p-0 sm:max-w-[517px]"
        style={
          { "--academy-accent": content.accentColor } as React.CSSProperties
        }
      >
        <VisuallyHidden.Root>
          <ModalTitle>{dialogTitle}</ModalTitle>
        </VisuallyHidden.Root>

        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border bg-surface-primary px-6 py-3 text-heading dark:bg-surface-elevated bg-[image:linear-gradient(90deg,color-mix(in_srgb,var(--academy-accent)_20%,transparent)_0%,transparent_60%)] dark:bg-[image:linear-gradient(90deg,color-mix(in_srgb,var(--academy-accent)_10%,transparent)_0%,transparent_60%)]">
          <div className="flex items-center gap-2">
            <Icon
              name="IconGraduateCap"
              size={18}
              className="shrink-0 text-current opacity-70"
            />
            <span className="text-lg font-medium leading-5">
              Arbi Academy
            </span>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex size-7 cursor-pointer items-center justify-center rounded-md text-contrast/40 transition-colors hover:bg-contrast/6 hover:text-contrast/70"
          >
            <Icon name="IconCrossMedium" size={20} className="text-current" />
          </button>
        </div>

        {/* Video / Thumbnail */}
        <div className="relative aspect-video w-full overflow-hidden bg-surface-inset">
          {!playing && (
            <div
              className="group absolute inset-0 z-10 cursor-pointer"
              onClick={handlePlay}
            >
              <Image
                src={content.thumbnailUrl}
                alt=""
                fill
                sizes="517px"
                className="object-cover"
                draggable={false}
              />
              <div className="absolute inset-0 bg-black/10" />

              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex size-14 items-center justify-center rounded-full bg-white/30 pl-1 backdrop-blur-sm transition-colors group-hover:bg-white/40">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 4.832c0-1.025 1.128-1.65 2.006-1.11l11.473 7.069c.825.508.825 1.71 0 2.218L8.006 20.078C7.128 20.618 6 19.993 6 18.968V4.832Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </div>
            </div>
          )}

          {playing && (
            <VideoPlayer
              ref={videoRef}
              src={content.videoUrl}
              poster={content.thumbnailUrl}
              accentColor={content.accentColor}
              autoPlay
              playsInline
            />
          )}
        </div>

        {/* Body */}
        <div className="flex flex-col gap-6 bg-surface-primary p-6 dark:bg-surface-elevated">
          <div className="flex flex-col gap-3">
            {/* Type + duration row */}
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium tracking-[-0.13px] text-contrast/60 dark:text-[color:var(--academy-accent)]">
                  {content.typeLabel}
                </span>
                <div className="flex items-center gap-1 text-contrast/60 dark:text-contrast/50">
                  <Icon
                    name="IconPlayCircle"
                    size={13}
                    className="text-current"
                  />
                  <span className="text-[13px] font-normal tracking-[-0.13px] text-current">
                    {content.duration}
                  </span>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl font-medium tracking-[-0.2px] text-heading">
                {content.title}
              </h3>
            </div>

            {/* Description */}
            <p className="text-sm font-normal leading-normal tracking-[-0.14px] text-contrast/60 dark:text-contrast/50">
              {content.description}
            </p>
          </div>

          {/* Bottom bar */}
          <div className="flex items-center gap-4 overflow-hidden rounded-lg border border-border bg-surface-secondary p-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:bg-surface-inset">
            <div className="flex min-w-0 flex-1 items-center">
              <span className="truncate text-sm font-medium text-heading">
                Learn more in detail about {content.typeLabel}
              </span>
            </div>

            <a
              href={content.docsUrl || "https://docs.neuroscale.ai"}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                buttonVariants({ variant: "outline", size: "sm" }),
                "relative shrink-0 gap-1",
              )}
            >
              <span className="whitespace-nowrap px-0.5">Go to Arbi Academy</span>
              <Icon name="IconArrowUpRight" size={14} className="text-current" />
              <span
                data-slot="button-inset"
                className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)] dark:shadow-[inset_0px_-1px_0px_0px_rgba(255,255,255,0.08)]"
              />
            </a>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}
